import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import { Button, EmailInput, Form, Modal, Tapable, toast, Tooltip } from '@feathr/components';
import RoleSelect from '@feathr/extender/App/Settings/Account/UsersAndRolesPage/Roles/RoleSelect';
import { useStore } from '@feathr/extender/state';
import { flattenError, logUserEvents } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

interface IBulkAssignModalProps {
  onClose: () => void;
}

function InviteUsersModal({ onClose }: Readonly<IBulkAssignModalProps>): JSX.Element {
  const { Users } = useStore();
  const [emails, setEmails] = useState<string | undefined>();
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [role, setRole] = useState<string>();

  const { t } = useTranslation();

  useEffect(() => {
    setEmailList(
      emails
        ?.split(',')
        .map((email) => email.trim())
        .filter((email) => email.length > 0) ?? [],
    );
  }, [emails]);

  function handleClose(): void {
    setEmails('');
    setEmailHadFocus(false);
    onClose();
  }

  async function handleInvite(): Promise<void> {
    if (role) {
      try {
        // If there are more than 10 users to invite, the invites are processed as an async job.
        if (emailList.length < 10) {
          await Users.invite({ emailList, role });
          toast(t('Sent invite links to {{count}} user', { count: emailList.length }), {
            type: ToastType.SUCCESS,
          });
        } else {
          Users.invite({ emailList, role });
          toast(t('Your invites are being processed. Check back later.'), { type: ToastType.INFO });
        }

        logUserEvents({
          'Invited user': {
            invited_users_emails: [...emailList],
          },
        });

        handleClose();
      } catch (error) {
        toast(t('There was an error sending your invites.\n{{- error}}', { error }), {
          type: ToastType.ERROR,
        });
      }
    }
  }

  function handleSelectRole(roleId: string): void {
    setRole(roleId);
  }

  const { emailValidationErrors, roleValidationErrors, validationErrors, hasValidEmail } =
    useMemo(() => {
      // Email validation
      const trimmedEmails =
        emails
          ?.split(',')
          .map((email) => email.trim())
          .filter((email) => email.length > 0) ?? [];

      let emailValidationErrors: string[] = [];
      if (trimmedEmails.length === 0) {
        emailValidationErrors = ['At least one email address is required.'];
      } else {
        const error = trimmedEmails
          .map((email: string) =>
            validate.single(email, {
              presence: { allowEmpty: false, message: '^Email addresses cannot be empty.' },
              email: { message: '^An email address is invalid.' },
            }),
          )
          .filter(Boolean);
        emailValidationErrors = flattenError(error) ?? [];
      }

      // Role validation
      const roleValidation = validate.single(role, {
        presence: { allowEmpty: false, message: '^A role must be selected.' },
      });
      const roleValidationErrors = flattenError(roleValidation) ?? [];

      // Combined errors
      const validationErrors = [...emailValidationErrors, ...roleValidationErrors];

      // Valid email check
      const hasValidEmail = emailValidationErrors.length === 0 && trimmedEmails.length > 0;

      return {
        emailValidationErrors,
        roleValidationErrors,
        validationErrors,
        hasValidEmail,
      };
    }, [emails, role]);

  const emailAddressHelpText = (
    <span>{t('You must add one or more emails separated by commas')}</span>
  );

  const roleHelpText = (
    <Trans t={t}>
      <Tooltip
        title={
          <p>
            <strong>Users</strong> can access any project.
            <br />
            <strong>Admins</strong> can access any project, invite new users, and change user roles.
          </p>
        }
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </Tooltip>
    </Trans>
  );

  function handleBlurEmail(): void {
    setEmailHadFocus(true);
  }

  const confirmButtonTooltip = useMemo(() => {
    if (!hasValidEmail) {
      return t('Please enter a valid email address');
    }
    if (validationErrors?.length > 0) {
      return validationErrors[0];
    }
    return null;
  }, [hasValidEmail, t, validationErrors]);
  const confirmButton = (
    <Tapable>
      <Button
        aria-label={t('Send invitations to entered email addresses')}
        disabled={!!validationErrors.length}
        onClick={handleInvite}
        tooltip={confirmButtonTooltip}
        type={'primary'}
      >
        {t('Invite')}
      </Button>
    </Tapable>
  );

  const cancelButton = (
    <Tapable>
      <Button
        aria-label={t('Cancel inviting users and close modal')}
        onClick={onClose}
        type={'secondary'}
      >
        {t('Cancel')}
      </Button>
    </Tapable>
  );

  return (
    <Modal
      allowOverflow={true}
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          {cancelButton} {confirmButton}
        </>
      }
      size={'default'}
      title={t('Invite Users')}
      transitionProps={{ duration: 1000, transition: 'slide-up', timingFunction: 'ease-out' }}
    >
      <Form label={t('Invite users')}>
        <EmailInput
          allowMultiple={true}
          helpPlacement={'top'}
          helpText={emailAddressHelpText}
          label={t('Email addresses')}
          name={'email_addresses'}
          onBlur={handleBlurEmail}
          onChange={setEmails}
          placeholder={t('Add one or more emails separated by commas')}
          t={t}
          type={'email'}
          validationError={emailHadFocus ? emailValidationErrors : []}
          value={emails}
        />
        <RoleSelect
          aria-describedby={roleValidationErrors.length > 0 ? 'invite-users-role-error' : undefined}
          data-testid={'role-select'}
          helpText={roleHelpText}
          onChange={handleSelectRole}
        />
      </Form>
    </Modal>
  );
}

export default observer(InviteUsersModal);
